import { ElementType, FormElement, ElementStyle, ContentRun } from './store/types';

export interface ToolboxItem {
  type: ElementType;
  label: string;
  category: 'Basic' | 'Media' | 'Layout' | 'Selection' | 'Input';
}

export const TOOLBOX_ITEMS: ToolboxItem[] = [
  { type: 'text', label: 'Text', category: 'Basic' },
  { type: 'label', label: 'Label', category: 'Basic' },
  { type: 'paragraph', label: 'Paragraph', category: 'Basic' },
  { type: 'heading', label: 'Heading', category: 'Basic' },

  { type: 'image', label: 'Image', category: 'Media' },

  { type: 'table', label: 'Table', category: 'Layout' },
  { type: 'hline', label: 'Horizontal Line', category: 'Layout' },
  { type: 'vline', label: 'Vertical Line', category: 'Layout' },
  { type: 'rect', label: 'Rectangle', category: 'Layout' },

  { type: 'checkbox', label: 'Checkbox', category: 'Selection' },
  { type: 'radio', label: 'Radio', category: 'Selection' },

  { type: 'textbox', label: 'Text Box', category: 'Input' },
  { type: 'textarea', label: 'Text Area', category: 'Input' },
];

const baseStyle: ElementStyle = {
  fontFamily: 'Arial',
  fontSize: 12,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textDecoration: 'none',
  color: '#000000',
  textAlign: 'left',
  background: 'transparent',
  borderWidth: 0,
  borderStyle: 'none',
  borderColor: '#000000',
  borderRadius: 0,
  padding: 4,
  lineHeight: 1.3,
};

let idCounter = 0;
export function nextElementId(): string {
  idCounter += 1;
  return `el_${Date.now().toString(36)}_${idCounter}`;
}

export function createElementDefaults(type: ElementType, x: number, y: number): FormElement {
  const common = {
    id: nextElementId(),
    type,
    x,
    y,
    rotation: 0,
    zIndex: 1,
    locked: false,
    visible: true,
    groupId: null,
    style: { ...baseStyle },
  };

  switch (type) {
    case 'text':
      return { ...common, width: 280, height: 28, content: [{ type: 'text', value: 'Text' }] };
    case 'label':
      return {
        ...common,
        width: 140,
        height: 24,
        style: { ...baseStyle, fontWeight: 'bold' },
        content: [{ type: 'text', value: 'Label:' }],
      };
    case 'paragraph':
      return {
        ...common,
        width: 300,
        height: 80,
        content: [{ type: 'text', value: 'Paragraph text goes here.' }],
      };
    case 'heading':
      return {
        ...common,
        width: 300,
        height: 36,
        style: { ...baseStyle, fontSize: 18, fontWeight: 'bold' },
        content: [{ type: 'text', value: 'Section Heading' }],
      };
    case 'image':
      return {
        ...common,
        width: 150,
        height: 100,
        style: { ...baseStyle, borderWidth: 1, borderStyle: 'dashed', borderColor: '#999999' },
        typeProps: { image: { fit: 'contain' } },
      };
    case 'table':
      return {
        ...common,
        width: 400,
        height: 120,
        style: { ...baseStyle, padding: 0 },
        typeProps: {
          table: {
            rows: 3,
            cols: 3,
            colWidths: [33.33, 33.33, 33.34],
            rowHeights: [40, 40, 40],
            cells: Array.from({ length: 3 }, () => Array.from({ length: 3 }, () => [] as ContentRun[])),
            merges: [],
          },
        },
      };
    case 'hline':
      return {
        ...common,
        width: 300,
        height: 2,
        style: { ...baseStyle, borderWidth: 1, borderStyle: 'solid', padding: 0 },
      };
    case 'vline':
      return {
        ...common,
        width: 2,
        height: 150,
        style: { ...baseStyle, borderWidth: 1, borderStyle: 'solid', padding: 0 },
      };
    case 'rect':
      return {
        ...common,
        width: 200,
        height: 120,
        style: { ...baseStyle, borderWidth: 1, borderStyle: 'solid', padding: 0 },
      };
    case 'checkbox':
      return {
        ...common,
        width: 160,
        height: 24,
        typeProps: { checkbox: { label: 'Checkbox label', checked: false } },
      };
    case 'radio':
      return {
        ...common,
        width: 160,
        height: 24,
        typeProps: { radio: { label: 'Option', checked: false, groupName: nextElementId() } },
      };
    case 'dropdown':
      return {
        ...common,
        width: 180,
        height: 28,
        style: { ...baseStyle, borderWidth: 1, borderStyle: 'solid', borderColor: '#c0c0c0' },
        typeProps: { dropdown: { options: ['Option 1', 'Option 2'] } },
      };
    case 'textbox':
      return {
        ...common,
        width: 180,
        height: 28,
        style: { ...baseStyle, borderWidth: 0, borderStyle: 'none' },
        bindings: {},
      };
    case 'textarea':
      return {
        ...common,
        width: 260,
        height: 80,
        style: { ...baseStyle, borderWidth: 0, borderStyle: 'none' },
        bindings: {},
      };
    case 'date':
      return {
        ...common,
        width: 140,
        height: 28,
        style: { ...baseStyle, borderWidth: 0, borderStyle: 'none' },
        bindings: {},
      };
    case 'signature':
      return {
        ...common,
        width: 220,
        height: 60,
        style: { ...baseStyle, borderWidth: 0, borderStyle: 'none' },
        typeProps: { signature: { placeholderText: 'Signature' } },
      };
    case 'pagenumber':
      return {
        ...common,
        width: 100,
        height: 20,
        style: { ...baseStyle, textAlign: 'center', fontSize: 10 },
        content: [{ type: 'text', value: 'Page {n}' }],
      };
    default:
      return { ...common, width: 100, height: 24, content: [{ type: 'text', value: '' }] };
  }
}
